import React, { useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import {
    Section, TextInput, NumberInput, SelectOne, SelectMany, CountrySelect, PhoneField, AddressFields, CheckLine,
    Explainer, FieldLabel, MUTED, BORDER, ORANGE, ORANGE_TINT,
} from './SignupFields.comp';
import { TEXT, LEGAL_PRIVACY_URL } from '../signup.text';

/*
 * The sign-up chatbot's forms. Each is controlled: `value` in, `onChange(next)` out,
 * shaped exactly as my-dq-api's pbdq_signup.service.js validates it. The server is
 * the validator; these forms only collect.
 */

// ─── DS-4 / DP-6 / WE-2 information registry ─────────────────────────────────

export const emptyRegistry = () => ({
    contact: { first_name: '', last_name: '', email: '', phone_country_code: '', phone_number: '', job_title: '' },
    organization: { name: '', type: '', type_other: '', primary_roles: [], primary_roles_other: '', country: '', address: {}, website: '' },
    legal_entity: { name: '', same_as_organization: false, registration_number: '', country: '', address: {}, same_as_organization_address: false },
    confirmations: { authorized: false, accurate: false, privacy: false, terms: false },
});

// emailReadOnly: the DQ Pro V2 sign-up, whose email is the sign-in email from its first step.
// stateByCountry: the organisation's State is a pick-list for its country (the sign-up).
export const RegistryForm = ({ catalog, value, onChange, legalEntity = false, confirmations = true, emailReadOnly = false, stateByCountry = false }) => {
    const set = (group, key) => (v) => onChange({ ...value, [group]: { ...value[group], [key]: v } });
    const c = value.contact;
    const o = value.organization;
    const l = value.legal_entity;
    return (
        <Box>
            <Section title="Your information">
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.75 }}>
                    <TextInput label="First name*" value={c.first_name} onChange={set('contact', 'first_name')}/>
                    <TextInput label="Last name*" value={c.last_name} onChange={set('contact', 'last_name')}/>
                </Box>
                <TextInput label="Business email*" type="email" value={c.email} onChange={set('contact', 'email')}
                    inputProps={emailReadOnly ? { readOnly: true } : undefined}
                    help={emailReadOnly ? 'Your sign-in email, from the previous step.' : undefined}/>
                <PhoneField label="Phone number*" value={[c.phone_country_code, c.phone_number].filter(Boolean).join(' ')}
                    onChange={(p) => onChange({ ...value, contact: { ...c, ...p } })}/>
                <TextInput label="Job title / role*" value={c.job_title} onChange={set('contact', 'job_title')}/>
            </Section>

            <Section title="Your organization">
                <TextInput label="Organization name*" value={o.name} onChange={set('organization', 'name')}/>
                <SelectOne label="Organization type*" options={catalog.ORGANIZATION_TYPES} value={o.type} onChange={set('organization', 'type')}/>
                {o.type === 'other' && <TextInput label="Please specify*" value={o.type_other} onChange={set('organization', 'type_other')}/>}
                <SelectMany label="Primary role / activity*" options={catalog.PRIMARY_ROLES} value={o.primary_roles} onChange={set('organization', 'primary_roles')}/>
                {o.primary_roles.includes('other') && <TextInput label="Other role / activity" value={o.primary_roles_other} onChange={set('organization', 'primary_roles_other')}/>}
                <CountrySelect label="Organization country*" value={o.country} onChange={stateByCountry
                    // A new country's state list: the old state no longer applies.
                    ? (v) => onChange({ ...value, organization: { ...o, country: v, address: { ...(o.address || {}), state: '' } } })
                    : set('organization', 'country')}/>
                <AddressFields label="Organization address*" value={o.address} onChange={set('organization', 'address')}
                    country={stateByCountry ? o.country : undefined} countryChosen={!!o.country}/>
                <TextInput label="Organization website" help={TEXT.WEBSITE_HELP} value={o.website} onChange={set('organization', 'website')} placeholder="https://"/>
            </Section>

            {legalEntity && (
                <Section title="Legal entity information">
                    {!l.same_as_organization && (
                        <TextInput label="Registered legal entity name*" help={TEXT.LEGAL_NAME_HELP} value={l.name} onChange={set('legal_entity', 'name')}/>
                    )}
                    <CheckLine checked={l.same_as_organization} onChange={set('legal_entity', 'same_as_organization')}>Same as the organization name above</CheckLine>
                    <TextInput label="Company or organization registration number*" value={l.registration_number} onChange={set('legal_entity', 'registration_number')}/>
                    <CountrySelect label="Country of registration*" value={l.country} onChange={set('legal_entity', 'country')}/>
                    {!l.same_as_organization_address && (
                        <AddressFields label="Registered address*" value={l.address} onChange={set('legal_entity', 'address')}/>
                    )}
                    <CheckLine checked={l.same_as_organization_address} onChange={set('legal_entity', 'same_as_organization_address')}>Same as the organization address above</CheckLine>
                </Section>
            )}

            {confirmations && (
                <Section title="Confirmation">
                    <CheckLine checked={value.confirmations.authorized} onChange={set('confirmations', 'authorized')}>{TEXT.CONFIRM_AUTHORIZED}</CheckLine>
                    <CheckLine checked={value.confirmations.accurate} onChange={set('confirmations', 'accurate')}>{TEXT.CONFIRM_ACCURATE}</CheckLine>
                    <CheckLine checked={value.confirmations.privacy} onChange={set('confirmations', 'privacy')}>
                        I have read and agree to the <a href={LEGAL_PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: ORANGE }}>Privacy Notice</a>.*
                    </CheckLine>
                </Section>
            )}
        </Box>
    );
};

// ─── DS-3 deployment details ─────────────────────────────────────────────────

/*
 * School or network (14 Oct 2026): no longer asked here. The organization type
 * decides it — "School" is a single school, every other type a school network —
 * on the server when the order is placed (pbdq_signup.service accountTypeOf).
 */
export const accountTypeOfOrganization = (type) => (type === 'school' ? 'school' : 'school_network');

const includedCredits = (learners) => (Number(learners) > 0 ? Math.ceil(Number(learners) / 10) : 0);

export const DeploymentForm = ({ catalog, solution, value, onChange }) => {
    const set = (k) => (v) => onChange({ ...value, [k]: v });
    const capabilities = catalog.CAPABILITIES_BY_SOLUTION[solution] || [];

    if (solution === 'P3') return <ValidationPrograms catalog={catalog} value={value} onChange={onChange}/>;

    const withCredits = ['P1', 'LC', 'P2B'].includes(solution);
    const withFramework = ['P2A', 'P2B'].includes(solution);
    return (
        <Section>
            {solution === 'P2A'
                ? <NumberInput label="Number of employees*" value={value.employees} onChange={set('employees')}/>
                : <NumberInput label="Number of learners*" value={value.learners} onChange={set('learners')}/>}
            {withCredits && (
                <Box>
                    <FieldLabel help={TEXT.INCLUDED_CREDITS}>LearnCreator credits included</FieldLabel>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: ORANGE }}>{includedCredits(value.learners).toLocaleString('en-US')} credits</Typography>
                </Box>
            )}
            {withCredits && (
                <SelectOne label={TEXT.ADDITIONAL_CREDITS.label} help={TEXT.ADDITIONAL_CREDITS.help}
                    options={catalog.CREDIT_BUNDLES} value={value.credit_bundle || 'none'} onChange={set('credit_bundle')}/>
            )}
            {withFramework && (
                <SelectOne label="FrameworkCreator tier*" options={catalog.FRAMEWORK_TIERS} value={value.framework_tier} onChange={set('framework_tier')}/>
            )}
            <SelectMany label={TEXT.CAPABILITIES.label} help={TEXT.CAPABILITIES.help} options={capabilities}
                value={value.capabilities || []} onChange={set('capabilities')} noneLabel="None"/>
            {/* DS-3.5 (both credits and framework) shows only the FrameworkCreator explainer */}
            {withFramework ? (
                <Explainer title={TEXT.FRAMEWORK_EXPLAINER.title}>{TEXT.FRAMEWORK_EXPLAINER.body}</Explainer>
            ) : withCredits && (
                <Explainer title={TEXT.CREDIT_EXPLAINER.title}>{TEXT.CREDIT_EXPLAINER.body}</Explainer>
            )}
        </Section>
    );
};

const emptyProgram = () => ({ name: '', tier: '', duration_hours: '', participants: '', audiences: [], audiences_other: '', languages: '', addons: [] });

/** DS-3.6 — one or more programs to validate. */
const ValidationPrograms = ({ catalog, value, onChange }) => {
    const programs = value.programs && value.programs.length ? value.programs : [emptyProgram()];
    const setProgram = (i, k) => (v) => onChange({ ...value, programs: programs.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
    return (
        <Box>
            {programs.map((p, i) => (
                <Box key={i} sx={{ mt: 2.5, p: 2, borderRadius: '12px', border: `1px solid ${BORDER}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 800 }}>Program {i + 1}</Typography>
                        {programs.length > 1 && (
                            <IconButton size="small" aria-label={`Remove program ${i + 1}`}
                                onClick={() => onChange({ ...value, programs: programs.filter((_, j) => j !== i) })}><CloseIcon style={{ fontSize: 18 }}/></IconButton>
                        )}
                    </Box>
                    <Section>
                        <TextInput label="Program name*" value={p.name} onChange={setProgram(i, 'name')}/>
                        <SelectOne label="Choose your validation tier*" options={catalog.VALIDATION_TIERS} value={p.tier} onChange={setProgram(i, 'tier')}/>
                        <NumberInput label="Program duration*" help={TEXT.DURATION_HELP} value={p.duration_hours} onChange={setProgram(i, 'duration_hours')} suffix="hours"/>
                        <NumberInput label="Expected number of participants in the program*" help={p.tier ? TEXT.PARTICIPANTS_HELP[p.tier] : undefined}
                            value={p.participants} onChange={setProgram(i, 'participants')}/>
                        <SelectMany label="Program audiences*" help="Which distinct audience or learner groups does this program serve? Select all that apply."
                            options={catalog.PROGRAM_AUDIENCES} value={p.audiences} onChange={setProgram(i, 'audiences')}/>
                        {p.audiences.includes('other') && <TextInput label="Please specify" value={p.audiences_other} onChange={setProgram(i, 'audiences_other')}/>}
                        <NumberInput label="Languages*" help="How many languages is the program delivered in?" value={p.languages} onChange={setProgram(i, 'languages')}/>
                        <SelectMany label="Optional add-ons*" options={catalog.VALIDATION_ADDONS} value={p.addons} onChange={setProgram(i, 'addons')} noneLabel="None"/>
                    </Section>
                </Box>
            ))}
            <Button onClick={() => onChange({ ...value, programs: [...programs, emptyProgram()] })}
                sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, color: ORANGE }}>+ Add another program</Button>
        </Box>
    );
};

/** DS-3.8 — #DQEveryChild. */
export const DqEveryChildForm = ({ catalog, value, onChange }) => {
    const set = (k) => (v) => onChange({ ...value, [k]: v });
    return (
        <Section>
            <SelectMany label="#DQEveryChild components*" options={catalog.DQEC_COMPONENTS} value={value.components || []} onChange={set('components')}/>
            <NumberInput label="Expected number of children*" value={value.expected_children} onChange={set('expected_children')}/>
            <NumberInput label="Expected number of schools or participating organizations*" value={value.expected_schools} onChange={set('expected_schools')}/>
            <SelectOne label="Rollout level*" options={catalog.DQEC_ROLLOUT} value={value.rollout_level} onChange={set('rollout_level')}/>
            <TextInput label="Target ages or year groups*" value={value.target_ages} onChange={set('target_ages')}/>
            <SelectOne label="Preferred delivery model*" options={catalog.DQEC_DELIVERY} value={value.delivery_model} onChange={set('delivery_model')}/>
            <TextInput label="Required language(s)*" value={value.languages} onChange={set('languages')}/>
            <SelectOne label="Do you require localization or co-branding?*" options={catalog.YES_NO_UNSURE} value={value.localization_cobranding} onChange={set('localization_cobranding')}/>
            <SelectMany label="Who will lead, fund, or sponsor the initiative?" options={catalog.DQEC_SPONSORS} value={value.sponsors || []} onChange={set('sponsors')}/>
            {(value.sponsors || []).includes('other') && <TextInput label="Please specify" value={value.sponsors_other} onChange={set('sponsors_other')}/>}
            <TextInput label="Target launch timeframe*" value={value.launch_timeframe} onChange={set('launch_timeframe')} placeholder="e.g. Q1 2027"/>
            <TextInput label="Tell us more." multiline
                help="What are you hoping to achieve through the initiative? Tell us about your intended outcomes, key partners, expected scale, available budget range, localization needs, or any other requirements that will help us understand the rollout."
                value={value.outcomes} onChange={set('outcomes')}/>
        </Section>
    );
};

// ─── DP-8 checklist ──────────────────────────────────────────────────────────

export const ChecklistForm = ({ catalog, value, onChange }) => {
    const set = (k) => (v) => onChange({ ...value, [k]: v });
    const C = catalog.CHECKLIST;
    return (
        <Section>
            <Explainer title="">{TEXT.DP8.help}</Explainer>
            <SelectOne label={TEXT.DP8.q1} options={C.q1_client_count} value={value.q1_client_count} onChange={set('q1_client_count')}/>
            <SelectMany label={TEXT.DP8.q2} options={C.q2_segments} value={value.q2_segments || []} onChange={set('q2_segments')}/>
            {(value.q2_segments || []).includes('other') && <TextInput label="Please specify" value={value.q2_other} onChange={set('q2_other')}/>}
            <SelectOne label={TEXT.DP8.q3} options={C.q3_intended_use} value={value.q3_intended_use} onChange={set('q3_intended_use')}/>
            <SelectOne label={TEXT.DP8.q4} options={C.q4_independence} value={value.q4_independence} onChange={set('q4_independence')}/>
            <SelectMany label={TEXT.DP8.q5} options={C.q5_responsibilities} value={value.q5_responsibilities || []} onChange={set('q5_responsibilities')}/>
            {(value.q5_responsibilities || []).includes('other') && <TextInput label="Please specify" value={value.q5_other} onChange={set('q5_other')}/>}
        </Section>
    );
};

// ─── DS-4.1 / DP-6.2.1 / DP-9.1 documents ────────────────────────────────────

const DOC_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const DOC_MAX_BYTES = 10 * 1024 * 1024;

/**
 * One document at a time: pick a file (and, for verification, a type and optional
 * link), then "Add". `onAdd({ doc_type, doc_type_other, link }, file)` uploads it;
 * `documents` lists what is already on the application.
 */
export const DocumentsForm = ({ catalog, withType = true, documents = [], onAdd, busy }) => {
    const [docType, setDocType] = useState('');
    const [docTypeOther, setDocTypeOther] = useState('');
    const [link, setLink] = useState('');
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const input = useRef(null);

    const pick = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        const ext = String(f.name).split('.').pop().toLowerCase();
        if (!DOC_EXTENSIONS.includes(ext)) { setError('Upload a PDF, JPG or PNG file.'); setFile(null); return; }
        if (f.size > DOC_MAX_BYTES) { setError('The file is larger than 10 MB.'); setFile(null); return; }
        setError('');
        setFile(f);
    };

    const add = async () => {
        const ok = await onAdd({ doc_type: withType ? docType : 'evidence', doc_type_other: docTypeOther, link }, file);
        if (ok) { setFile(null); setLink(''); setDocType(''); setDocTypeOther(''); }
    };

    const canAdd = !busy && (withType ? (!!docType && !!file) : (!!file || !!link));

    return (
        <Box>
            {documents.length > 0 && (
                <Section title="Added">
                    {documents.map((d, i) => (
                        <Typography key={i} sx={{ fontSize: '0.85rem' }}>
                            ✓ {d.file_name || d.link}{d.doc_type && d.doc_type !== 'evidence' ? ` — ${catalog.DOCUMENT_TYPES.find((t) => t.value === d.doc_type)?.label || d.doc_type}` : ''}
                        </Typography>
                    ))}
                </Section>
            )}
            <Section title={documents.length ? 'Add another document' : undefined}>
                {withType && <SelectOne label="Document type*" options={catalog.DOCUMENT_TYPES} value={docType} onChange={setDocType}/>}
                {withType && docType === 'other' && <TextInput label="Please specify*" value={docTypeOther} onChange={setDocTypeOther}/>}
                <Box>
                    <FieldLabel help={TEXT.DOCUMENT_FILE_HELP}>{withType ? 'File*' : 'File'}</FieldLabel>
                    <input ref={input} type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={pick}/>
                    <Box role="button" tabIndex={0} onClick={() => input.current && input.current.click()}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current && input.current.click(); } }}
                        sx={{ p: 2, textAlign: 'center', borderRadius: '10px', cursor: 'pointer', border: `1.5px dashed ${file ? ORANGE : '#CBD5E1'}`, background: file ? ORANGE_TINT : 'var(--brand-bg, #F9FAFB)' }}>
                        <CloudUploadIcon style={{ color: ORANGE }}/>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{file ? file.name : 'Choose a file'}</Typography>
                    </Box>
                    {error && <Typography sx={{ fontSize: '0.8rem', color: '#dc2626', mt: 0.5 }}>{error}</Typography>}
                </Box>
                <TextInput label="Link" help={TEXT.DOCUMENT_LINK_HELP} value={link} onChange={setLink} placeholder="https://"/>
                <Box>
                    <Button variant="outlined" onClick={add} disabled={!canAdd}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', color: ORANGE, borderColor: ORANGE }}>
                        {busy ? 'Uploading…' : 'Add document'}
                    </Button>
                </Box>
            </Section>
        </Box>
    );
};

export const NoteField = ({ label, value, onChange }) => (
    <Box sx={{ mt: 2.5 }}>
        <FieldLabel>{label}</FieldLabel>
        <TextField fullWidth multiline minRows={3} size="small" value={value} onChange={(e) => onChange(e.target.value.slice(0, 5000))}/>
    </Box>
);

// ─── DS-5 order review ───────────────────────────────────────────────────────

// organizationType: before the order is placed, school or network from the organization type.
export const OrderReview = ({ quote, organization, organizationType, solutionLabel, deployment, catalog }) => {
    const fmt = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
    const accountType = deployment?.account_type || (organizationType ? accountTypeOfOrganization(organizationType) : null);
    const accountLabel = TEXT.ACCOUNT_TYPE.options.find((o) => o.value === accountType)?.label;
    const tierLabel = catalog?.FRAMEWORK_TIERS?.find((o) => o.value === deployment?.framework_tier)?.label;
    return (
        <Box>
            <Section title="Your order">
                <Box sx={{ fontSize: '0.88rem', lineHeight: 1.8 }}>
                    <div>Organization: <strong>{organization}</strong></div>
                    <div>Solution: <strong>{solutionLabel}</strong></div>
                    {accountLabel && <div>Account: <strong>{accountLabel}</strong></div>}
                    {quote.licences > 0 && <div>Deployment: <strong>{quote.licences.toLocaleString('en-US')} {quote.solution === 'P2A' || quote.solution === 'P2B' ? 'employee' : 'learner'} licenses available for 12 months</strong></div>}
                    {quote.solution === 'P3' && <div>Deployment: <strong>{quote.programs.length} program{quote.programs.length === 1 ? '' : 's'}</strong></div>}
                    {tierLabel && <div>Configuration: <strong>FrameworkCreator, {tierLabel}</strong></div>}
                    {(quote.credits_included > 0 || quote.credits_purchased > 0) && (
                        <div>Credits: <strong>{quote.credits_included.toLocaleString('en-US')} included, {quote.credits_purchased.toLocaleString('en-US')} purchased</strong></div>
                    )}
                </Box>
            </Section>
            <Section title="Your total">
                <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
                    {quote.lines.map((l) => (
                        <Box key={l.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 1.75, py: 1, borderBottom: `1px solid ${BORDER}` }}>
                            <Typography sx={{ fontSize: '0.86rem' }}>{l.label}</Typography>
                            <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, whiteSpace: 'nowrap', color: l.quoted_separately ? MUTED : 'inherit' }}>
                                {l.quoted_separately ? 'Quoted separately' : fmt(l.amount_usd)}
                            </Typography>
                        </Box>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.75, py: 1.25, background: 'var(--brand-bg, #F9FAFB)' }}>
                        <Typography sx={{ fontWeight: 800 }}>Due today</Typography>
                        <Typography sx={{ fontWeight: 800, color: ORANGE }}>{fmt(quote.total_usd)} USD</Typography>
                    </Box>
                </Box>
                {quote.has_quoted_separately && <Typography sx={{ fontSize: '0.8rem', color: MUTED }}>{TEXT.QUOTED_SEPARATELY}</Typography>}
            </Section>
        </Box>
    );
};
